import React, { useEffect, useState, useContext, } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Box, Typography, TextField, useMediaQuery, useTheme, Paper,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { store } from '../../redux/store';
import * as patientActions from '../../redux/actions/patientActions';
import CustomTextField from '../../commons/CustomTextField';
import TableSelBox from '../../commons/TableSelBox';
import moment from 'moment';
import { ConsultationContext, } from '../../context/ConsultationContext';
import { socket } from '../../socket/socket';
import usePermissions from '../../utils/permissions';
import Select from "react-select";
import { formatWeeks } from '../../utils/formatWeekAndDays';

const COLUMNS_TO_SHOW = 4;
const COLUMNS_TO_SHOW_MOBILE = 1;

const multiSelectStyle = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        svg: {
            height: 12,
            width: 12,
        },
    }),
    indicatorsContainer: (base) => ({
        ...base,
        display: 'none',
        height: '100%',
    }),
    control: (base) => ({
        ...base,
        border: 'none',
        padding: '0',
        borderRadius: 4,
        minHeight: '29px',
        border: "1px solid #919191"
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: '10px',
    }),
}

export default function ObservationTable() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const columnsToShow = isMobile ? COLUMNS_TO_SHOW_MOBILE : COLUMNS_TO_SHOW;
    const { patientId, consultationId } = useParams();
    const patientReducer = useSelector(state => state.patientReducer);
    const { setChildData } = useContext(ConsultationContext);
    const [sections, setSections] = useState([]);
    const { hasPermission } = usePermissions();
    const [sectionIndexes, setSectionIndexes] = useState({});
    const patientType = patientReducer?.patientInfo?.patientType || 'OBSTETRIC'

    useEffect(() => {
        if (patientId) {
            store.dispatch(patientActions.getPatientConsultationDetailsRequestAction(consultationId));
        }
    }, [patientId])

    useEffect(() => {
        if (!patientId) return;
        const handleCommandOutput = (data) => {
            console.log("data came on event", data)
            const [, , rawCardType] = data?.data?.originActionType?.split('.');

            let cardType = rawCardType;
            let cardKey = rawCardType;
            if (rawCardType.includes(':')) {
                const [cardCategory, id] = rawCardType.split(':');
                cardType = cardCategory
                cardKey = rawCardType
            }
            if (consultationId !== data?.data?.consultationId) {
                return
            }
            if (
                data?.actionType === "commandOutput.refreshWebDashboard"
            ) {
                if (cardKey.includes('examination:') || cardType == "condition"||cardType == "conditions") {
                    store.dispatch(patientActions.getPatientConsultationDetailsRequestAction(consultationId));
                }
                // if (cardType == "generalExamination" || cardType == "respiratorySystem" || cardType == "perAbdomen" || cardType == "perVagina" || cardType == "fetus" || cardType == "injection" || cardType == "examinationFinding" || cardType=="conditions") {
                // }
            }
        };
        socket.on("commandOutput", handleCommandOutput);
    }, [patientId]);


    const updateStart = (code, value) => {
        setSectionIndexes(prev => ({
            ...prev,
            [code]: value
        }));
    };

    useEffect(() => {
        if (patientReducer.consultations) {
            setSections(
                patientReducer.consultations.sections || []
            );
        }
    }, [patientReducer.consultations]);


    const [edit, setEdit] = useState(true)
    const [savedConsultation, setSavedConsultation] = useState({})
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');
        if (mode) {
            setEdit(mode == "view" ? false : hasPermission("OBSERVATIONS_CREATE") ? true : false);
        }
    }, [location.search]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setChildData(savedConsultation);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [savedConsultation]);

    const handleChange = (consultationId, question, answer) => {
        setSavedConsultation(prev => {
            const prevResponses = prev[consultationId]?.responses || [];
            const index = prevResponses.findIndex(r => r.questionId === question.id);

            const newResponses = [...prevResponses];
            if (index >= 0) {
                newResponses[index] = { ...newResponses[index], answer };
            } else {
                newResponses.push({ questionId: question.id, answer });
            }

            return {
                ...prev,
                [consultationId]: {
                    ...prev[consultationId],
                    responses: newResponses,
                },
            };
        });
    };

    const fetchComponent = (obj, index, row, question, mainRow) => {
        const type = obj?.type
        if (type == "TEXT") {
            return (
                edit && mainRow.isCurrent && !question.readOnly ?
                    <CustomTextField
                        value={obj.response}
                        setValue={(val) => {
                            handleChange(
                                row.consultationId,
                                question,
                                val
                            )
                        }}
                        edit={edit && mainRow.status !== "COMPLETED"}
                    />
                    : <Typography textAlign={'center'} sx={{fontSize: '13px',  }}>
                        {obj.response  || '-'}
                    </Typography>
            )
        }
        else if (type == "SINGLE_CHOICE") {
            return (
                edit && mainRow.isCurrent ?
                    <TableSelBox
                        options={obj.options == null ? [] : obj.options}
                        value={obj.response !== null ? obj.response : ''}
                        setValue={(val) => {
                            handleChange(
                                row.consultationId,
                                question,
                                val)
                        }}
                        type={type}
                        edit={edit && mainRow.status !== "COMPLETED"}
                    />
                    :
                    <Typography textAlign={'center'} sx={{ fontSize: '13px', }}>
                        {obj.response || '-'}
                    </Typography>
            )
        }
        else if (type == "TEXTAREA") {
            return edit && mainRow.isCurrent && mainRow.status !== "COMPLETED" ? <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder='Type here..'
                value={obj.response}
                setValue={(val) => {
                    handleChange(
                        row.consultationId,
                        question,
                        val)
                }}
                sx={{
                    backgroundColor: 'white',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.15)',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': {
                        minHeight: '145px',
                        borderRadius: '10px',
                        '& fieldset': {
                            border: 'none',
                        },
                        '& input': {
                            padding: '0 14px',
                            minHeight: '145px',
                            boxSizing: 'border-box',
                        },
                    },
                }}
            /> :
                <Typography textAlign={'center'} sx={{ fontSize: '13px',  }}>
                    {obj.response || '-'}
                </Typography>
        }
        else if (type == "DATE") {
            return (
                edit && mainRow.isCurrent && !question.readOnly ?
                    <TextField
                        type="date"
                        fullWidth
                        variant="standard"
                        InputProps={{
                            disableUnderline: true,
                        }}
                        value={obj.response ? moment(obj.response).format('YYYY-MM-DD') : ''}
                        onChange={(e) => {
                            handleChange(
                                row.consultationId,
                                question,
                                e.target.value
                            )
                        }}
                        inputProps={{
                            min: obj.minDate,
                            onClick: (e) => {
                                if (e.target.showPicker) {
                                    e.target.showPicker();
                                }
                            }
                        }}
                        disabled={!(edit && mainRow.status !== "COMPLETED")}
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: '5px',
                            border: '1px solid #919191',
                            margin: '0px 1px',
                            px: 0,
                            '& input': {
                                textAlign: 'center',
                                fontSize: '12px',
                                padding: '2px 0px !important',
                                cursor: 'pointer'
                            },
                        }}
                    />
                    : <Typography textAlign={'center'} sx={{ fontSize: '13px' }}>
                        {obj.response ? moment(obj.response).format('DD MMM YYYY') : '-'}
                    </Typography>
            )
        }
        else if (type == "MULTI_CHOICE") {

            const options =
                (obj.options || []).map(item => ({
                    label: item,
                    value: item,
                }));

            const localResponse =
                savedConsultation?.[row.consultationId]?.responses?.find(
                    r => r.questionId === question.id
                );

            const apiResponse =
                mainRow.responses.find(
                    r => r.questionId === question.id
                );

            const mixedObject = {
                ...question,
                response: localResponse
                    ? localResponse.answer
                    : (apiResponse?.answer ?? ""),
            };

            return <Select
                isMulti
                options={options}
                value={options.filter(option =>
                    (mixedObject.response || []).includes(option.value)
                )}
                onChange={(selectedOptions) => {
                    console.log("slec", selectedOptions)
                    handleChange(
                        row.consultationId,
                        question,
                        selectedOptions
                            ? selectedOptions.map(x => x.value)
                            : [])
                }
                }
                placeholder=""
                isDisabled={!edit}
                isSearchable={false}
                isClearable={false}
                styles={multiSelectStyle}
                menuPortalTarget={document.body}
            />
        }
    }

    let lmpQuestionId = null;
    for (const sec of sections) {
        const q = sec.questions.find(q => q.label && (q.label.trim().toLowerCase() === 'clinical lmp' || q.label.trim().toLowerCase() === 'lmp'));
        if (q) {
            lmpQuestionId = q.id;
            break;
        }
    }

    return (
        <>
            {<TableContainer>
                {<Box
                    display="flex"
                    flexDirection="column"
                >
                    {sections.map((section) => {
                        const start = sectionIndexes[section.id] || 0;
                        const visibleRecords = [...section.records]
                            .slice(start, start + columnsToShow);

                        while (visibleRecords.length < columnsToShow) {
                            visibleRecords.push(null);
                        }
                        const canLeft = start > 0;
                        const canRight = start + columnsToShow < section.records.length;
                        return (
                            <Paper
                                key={section.id}
                                sx={{
                                    mb: 2,
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: "1px solid #E6E8EC",
                                    boxShadow: 'none'
                                }}
                            >
                                {/* Header */}
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    px={2}
                                >
                                    <Typography sx={{ fontSize: { xl: '19px', lg: '17px', md: '15px' }, fontWeight: 600 }}>
                                        {section.title}
                                    </Typography>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <IconButton
                                            disabled={!canLeft}
                                            onClick={() =>
                                                updateStart(section.id, start - 1)
                                            }
                                        >
                                            <ChevronLeft />
                                        </IconButton>
                                        <IconButton
                                            disabled={!canRight}
                                            onClick={() =>
                                                updateStart(section.id, start + 1)
                                            }
                                        >
                                            <ChevronRight />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Table sx={{
                                    width: "100%",
                                    tableLayout: "fixed",
                                    '& .MuiTableCell-root': { py: 0.75 }
                                }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ border:'none'}}>
                                            </TableCell>

                                            {visibleRecords.map((record, index) => (
                                                <TableCell key={index} align="center" sx={{ border:'none'}}>
                                                    {record ? (
                                                        <>
                                                            <Typography sx={{ fontSize: { xl: '15px', lg: '13px', md: '11px' } }}>
                                                                {patientType == "GYNECOLOGY" ? moment(record.createdAt).format("DD MMM YYYY"): formatWeeks(record.clinicalWeek)}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        null
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {section.questions
                                            .sort((a, b) => a.order - b.order)
                                            .map((question) => (
                                                <TableRow key={question.id}>
                                                    <TableCell sx={{ border:'none'}}>
                                                        <Typography sx={{ fontSize: { xl: '16px', lg: '15px', md: '12px' } }}>

                                                            {question.label}
                                                        </Typography>
                                                    </TableCell>

                                                    {visibleRecords.map((record, index) => {
                                                        if (!record) {
                                                            return (
                                                                <TableCell key={`${question.id}-${index}`} sx={{ border:'none'}}>
                                                                </TableCell>
                                                            );
                                                        }
                                                        const response =
                                                            record.responses.find(
                                                                (x) =>
                                                                    x.questionId === question.id
                                                            );
                                                            
                                                        const savedResponse = savedConsultation[record.consultationId]?.responses?.find(
                                                            (x) => x.questionId === question.id
                                                        );

                                                        let finalResponse = savedResponse ? savedResponse.answer : response?.answer ?? "";
                                                        
                                                        let lmpDate = patientReducer?.patientInfo?.lmp;
                                                        
                                                        if (lmpQuestionId) {
                                                            const sLmp = savedConsultation[record.consultationId]?.responses?.find(x => x.questionId === lmpQuestionId)?.answer;
                                                            const rLmp = record.responses.find(x => x.questionId === lmpQuestionId)?.answer;
                                                            if (sLmp || rLmp) {
                                                                lmpDate = sLmp || rLmp;
                                                            }
                                                        }

                                                        const obj = {
                                                            ...question,
                                                            response: finalResponse,
                                                            minDate: (lmpDate && question.id !== lmpQuestionId) ? moment(lmpDate).format('YYYY-MM-DD') : undefined,
                                                        };

                                                        return (
                                                            <TableCell
                                                                key={`${record.createdAt}-${index}`}
                                                                align="center"
                                                                sx={{ border:'none'}}
                                                            >
                                                                {record
                                                                    ? fetchComponent(
                                                                        obj,
                                                                        index,
                                                                        record,
                                                                        question,
                                                                        record
                                                                    )
                                                                    : "-"}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        );
                    })}
                </Box>}
            </TableContainer>
            }
        </>
    );
}
